import React from 'react';
import styled from 'styled-components';
import { defaultTheme } from '../theme';

interface ClipSaveConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoId?: string;
  error?: string;
  isLoading?: boolean;
}

const ClipSaveConfirmationModal: React.FC<ClipSaveConfirmationModalProps> = ({
  isOpen,
  onClose,
  videoId,
  error,
  isLoading
}) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {isLoading ? 'Saving Clip...' : videoId ? 'Clip Saved!' : 'Error'}
          </ModalTitle>
        </ModalHeader>
        
        <ModalBody>
          {isLoading && (
            <LoadingSpinner>
              <Spinner />
              <LoadingText>Creating clip from selection...</LoadingText>
            </LoadingSpinner>
          )}
          
          {videoId && !isLoading && (
            <SuccessMessage>
              <CheckIcon>✓</CheckIcon>
              <div>
                <p>Your clip has been saved successfully!</p>
                <VideoIdText>Video ID: {videoId}</VideoIdText>
              </div>
            </SuccessMessage>
          )}
          
          {error && !isLoading && (
            <ErrorMessage>
              <ErrorIcon>⚠</ErrorIcon>
              <div>
                <p>Failed to save clip</p>
                <ErrorText>{error}</ErrorText>
              </div>
            </ErrorMessage>
          )}
        </ModalBody>
        
        {!isLoading && (
          <ModalFooter>
            <CloseButton onClick={onClose}>
              {videoId ? 'Done' : 'Close'}
            </CloseButton>
          </ModalFooter>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default ClipSaveConfirmationModal;

// Styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background-color: ${defaultTheme.colors.background};
  border: 2px solid ${defaultTheme.colors.border};
  border-radius: ${defaultTheme.borderRadius.md};
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.5);
  min-width: 400px;
  max-width: 500px;
  overflow: hidden;
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const ModalHeader = styled.div`
  padding: ${defaultTheme.spacing.md};
  border-bottom: 1px solid ${defaultTheme.colors.border};
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${defaultTheme.colors.text};
`;

const ModalBody = styled.div`
  padding: ${defaultTheme.spacing.lg};
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalFooter = styled.div`
  padding: ${defaultTheme.spacing.md};
  border-top: 1px solid ${defaultTheme.colors.border};
  display: flex;
  justify-content: flex-end;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${defaultTheme.spacing.md};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${defaultTheme.colors.border};
  border-top-color: ${defaultTheme.colors.active};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  margin: 0;
  color: ${defaultTheme.colors.textSecondary};
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${defaultTheme.spacing.md};
  color: ${defaultTheme.colors.text};
`;

const CheckIcon = styled.div`
  width: 48px;
  height: 48px;
  background-color: #48BB78;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
`;

const VideoIdText = styled.p`
  margin: 4px 0 0 0;
  font-size: 12px;
  color: ${defaultTheme.colors.textSecondary};
  font-family: monospace;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${defaultTheme.spacing.md};
  color: ${defaultTheme.colors.text};
`;

const ErrorIcon = styled.div`
  width: 48px;
  height: 48px;
  background-color: #F56565;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
`;

const ErrorText = styled.p`
  margin: 4px 0 0 0;
  font-size: 14px;
  color: #F56565;
`;

const CloseButton = styled.button`
  background-color: ${defaultTheme.colors.active};
  color: ${defaultTheme.colors.background};
  border: none;
  border-radius: ${defaultTheme.borderRadius.sm};
  padding: ${defaultTheme.spacing.xs} ${defaultTheme.spacing.md};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background-color: #6c9ba5;
  }
`;